import {
  Button,
  column,
  Css,
  dateColumn,
  type GridColumn,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  type ModalProps,
  simpleDataRows,
  simpleHeader,
  type SimpleHeaderAndData,
  useModal,
} from "@homebound/beam";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { dateCell } from "src/components";
import {
  type TakeoffsUsageModalQuery,
  useTakeoffsUsageModalQuery,
} from "src/routes/libraries/material-catalog/components/takeoffs-usage-modal/TakeoffsUsageModal.generated";
import { createPlanPackageTakeoffUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";

type TakeoffsUsageModalProps = { materialVariantId: string };

type TakeoffUsageReadyPlan = TakeoffsUsageModalQuery["materialVariant"]["takeoffUsages"][number];
type Row = SimpleHeaderAndData<TakeoffUsageReadyPlan>;
type ProjectUsage = TakeoffsUsageModalQuery["materialVariant"]["projectUsages"][number];
type ProjectRow = SimpleHeaderAndData<ProjectUsage>;

export const takeoffsUsageModalOptions: Pick<ModalProps, "size" | "drawHeaderBorder"> = {
  size: "lg",
  drawHeaderBorder: true,
};

export function TakeoffsUsageModal(props: TakeoffsUsageModalProps) {
  const { materialVariantId } = props;
  const { closeModal } = useModal();
  const query = useTakeoffsUsageModalQuery({ variables: { id: materialVariantId } });

  return (
    <>
      <ModalHeader>Usage Details</ModalHeader>
      <ModalBody>
        {queryResult(query, {
          data: (data) => (
            <TakeoffsUsageList
              takeoffUsages={data.materialVariant.takeoffUsages}
              projectUsages={data.materialVariant.projectUsages}
            />
          ),
        })}
      </ModalBody>
      <ModalFooter>
        <Button label="Ok" onClick={closeModal} />
      </ModalFooter>
    </>
  );
}

type TakeoffsUsageListProps = {
  takeoffUsages: TakeoffsUsageModalQuery["materialVariant"]["takeoffUsages"];
  projectUsages: ProjectUsage[];
};

type UsageSection = { title: string; testId: string } & (
  { kind: "takeoff"; items: TakeoffUsageReadyPlan[] } | { kind: "project"; items: ProjectUsage[] }
);

function TakeoffsUsageList(props: TakeoffsUsageListProps) {
  const { takeoffUsages, projectUsages } = props;
  const sections = useMemo(
    () =>
      [
        {
          kind: "takeoff" as const,
          title: "Takeoff",
          testId: "planPackages",
          items: takeoffUsages
            .filter((rp) => rp.__typename === "PlanPackage")
            .sortBy((rp) => rp.displayName ?? rp.name),
        },
        {
          kind: "takeoff" as const,
          title: "Design Package",
          testId: "designPackages",
          items: takeoffUsages
            .filter((rp) => rp.__typename === "DesignPackage")
            .sortBy((rp) => rp.displayName ?? rp.name),
        },
        {
          kind: "takeoff" as const,
          title: "Product Offering",
          testId: "productOfferings",
          items: takeoffUsages
            .filter((rp) => rp.__typename === "ProductOffering")
            .sortBy((rp) => rp.displayName ?? rp.name),
        },
        {
          kind: "project" as const,
          title: "Project",
          testId: "projects",
          items: projectUsages.sortBy((project) => project.displayName),
        },
      ].filter((section) => section.items.nonEmpty),
    [takeoffUsages, projectUsages],
  );

  return (
    <div data-testid={sections.nonEmpty ? "takeoffsUsageList" : undefined}>
      {sections.nonEmpty && <div css={Css.gray600.sm.my3.$}>This material is used in the following locations:</div>}
      <div css={Css.df.fdc.gap2.$}>
        {sections.isEmpty && <div css={Css.gray700.$}>Not used in any takeoffs.</div>}
        {sections.map((section) => (
          <TakeoffsUsageSection key={section.testId} section={section} />
        ))}
      </div>
    </div>
  );
}

type TakeoffsUsageSectionProps = { section: UsageSection };

/** Renders each usage section with the columns for its record type. */
function TakeoffsUsageSection(props: TakeoffsUsageSectionProps) {
  const { section } = props;
  const { title, testId, items } = section;
  return (
    <div data-testid={testId}>
      <div css={Css.lg.fw6.mb2.$}>{`${items.length} ${title}${items.length === 1 ? "" : "s"}`}</div>
      {section.kind === "project" ? (
        <GridTable
          columns={projectColumns}
          rows={[simpleHeader, ...simpleDataRows(section.items)]}
          style={{ allWhite: true, bordered: true }}
        />
      ) : (
        <GridTable
          columns={columns}
          rows={[simpleHeader, ...simpleDataRows(section.items)]}
          style={{ allWhite: true, bordered: true }}
        />
      )}
    </div>
  );
}

type TakeoffUsageLinkProps = { readyPlan: TakeoffUsageReadyPlan };

function TakeoffUsageLink(props: TakeoffUsageLinkProps) {
  const { readyPlan } = props;
  const label = readyPlan.displayName ?? readyPlan.name;
  const to = getTakeoffUsageUrl(readyPlan);
  if (!to) {
    return <span>{label}</span>;
  }
  return (
    <Link css={Css.blue700.$} data-testid="takeoffsUsageItem" to={to} target="_blank">
      {label}
    </Link>
  );
}

const columns: GridColumn<Row>[] = [
  column<Row>({
    header: "Name",
    data: (readyPlan) => ({
      content: () => <TakeoffUsageLink readyPlan={readyPlan} />,
      value: readyPlan.displayName ?? readyPlan.name,
    }),
  }),
  column<Row>({ header: "Owner", data: ({ owner }) => owner?.name }),
  dateColumn<Row>({ header: "Last Edited", data: ({ updatedAt }) => dateCell(updatedAt, { dateFormatStyle: "long" }) }),
];

const projectColumns: GridColumn<ProjectRow>[] = [
  column<ProjectRow>({
    header: "Name",
    data: (project) => ({
      content: () => (
        <Link css={Css.blue700.$} data-testid="projectUsageItem" to={project.blueprintUrl.path} target="_blank">
          {project.displayName}
        </Link>
      ),
      value: project.displayName,
    }),
  }),
];

function getTakeoffUsageUrl(readyPlan: TakeoffUsageReadyPlan): string | undefined {
  if (readyPlan.__typename === "PlanPackage") {
    // Plan Packages also have a blueprintURL; but it takes you to the plan overview
    // This will take the user straight to the takeoff manager to reduce an extra click
    return createPlanPackageTakeoffUrl(readyPlan.id, readyPlan.aggregateLatest.id);
  }
  if (readyPlan.__typename === "DesignPackage" || readyPlan.__typename === "ProductOffering") {
    return readyPlan.blueprintUrl.path;
  }
  return undefined;
}
